<script setup>
import {reactive, toRaw, ref, h, watch, onMounted, nextTick} from 'vue'
import {
  Card,
  Form,
  FormItem,
  Space,
  Select,
  Button,
  TabPane,
  Tabs,
  DatePicker,
  Table,
  Checkbox,
  CheckboxGroup,
  CheckableTag,
  Row,
  Col,
  Alert,
  Tooltip,
  Spin,
  message,
  Radio,
  RadioGroup
} from 'ant-design-vue'
import {RetweetOutlined, SwapRightOutlined} from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import {getWeekNumber, getTicketNumber} from '@/utils'
import {
  fetchTicketSearch,
  fetchStationAll,
  fetchTrainStation
} from '@/service/index'
import {SEAT_CLASS_TYPE_LIST, TRAIN_BRAND_LIST, TRAIN_TAG} from '@/constants'
import {useRoute, useRouter} from 'vue-router'

const useForm = Form.useForm
const route = useRoute()
const router = useRouter()

const carRangeTime = [
  {value: 0, label: '00:00-24:00'},
  {value: 1, label: '00:00-06:00'},
  {value: 2, label: '06:00-12:00'},
  {value: 3, label: '12:00-18:00'},
  {value: 4, label: '18:00-24:00'}
]

const headSearch = reactive({
  fromStation: '北京',
  toStation: '上海',
  departureDate: dayjs().add(7, 'day'),
  arrival_date: '',
  car_type: [],
  departure_stations: [],
  arrival_stations: [],
  departure: [],
  arrival: [],
  seat: [],
  /** 单程 single | 往返 round | 中转 transfer */
  tripMode: 'single',
  returnDate: dayjs().add(7, 'day'),
  /** 发车时段 0 全天 1-4 对应 carRangeTime */
  departureTimeSlot: 0,
  /** 列表排序 */
  sortKey: 'default'
})

const state = reactive({
  seatClassTypeListSelect: null,
  trainBrandListSelect: null,
  trainList: [],
  stationList: [],
  trainStationList: [],
  loading: false,
  pageLoading: false,
  searchLoading: false,
  returnTrainList: [],
  quoteTimestamp: null
})

const rowState = reactive({
  rawTrainList: []
})

const tablePagination = reactive({
  current: 1,
  pageSize: 50,
  showSizeChanger: true,
  pageSizeOptions: ['50', '100', '200'],
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
  onChange: (page, pageSize) => {
    tablePagination.current = page
    tablePagination.pageSize = pageSize
  }
})

const filterApplying = ref(false)

/** 解析发车时刻中的小时，兼容「14:19」「14:19开」 */
const parseDepartureHour = (t) => {
  if (!t) return 0
  const m = String(t).match(/(\d{1,2}):(\d{2})/)
  return m ? parseInt(m[1], 10) : 0
}

const trainMinPrice = (tr) => {
  const arr = tr?.seatClassList?.map((s) => s.price).filter((x) => x != null) ?? []
  return arr.length ? Math.min(...arr) : 1e9
}

/** 从车次号首字母推断品牌编码（与后端 TrainBrand 一致） */
const inferBrandCodeFromTrainNumber = (trainNumber) => {
  if (!trainNumber) return null
  const prefix = String(trainNumber).charAt(0).toUpperCase()
  const map = { G: 0, C: 0, D: 1, Z: 2, T: 3, K: 4 }
  return map[prefix] ?? null
}

/** 合并接口返回的品牌列表与结果集中实际出现的车次类型 */
const mergeTrainBrandList = (apiList, trainList) => {
  const brandSet = new Set(Array.isArray(apiList) ? apiList : [])
  ;(trainList || []).forEach((item) => {
    if (item.trainBrand) {
      item.trainBrand.split(',').forEach((code) => {
        const n = Number(code)
        if (!Number.isNaN(n)) brandSet.add(n)
      })
    }
    const inferred = inferBrandCodeFromTrainNumber(item.trainNumber)
    if (inferred != null) brandSet.add(inferred)
  })
  return [...brandSet].sort((a, b) => a - b)
}

/** 解析条目上的品牌编码（标签类 6/7 来自 trainBrand，主类型以车次号为准） */
const getTrainBrandCodes = (item) => {
  const codes = new Set()
  if (item.trainBrand) {
    item.trainBrand.split(',').forEach((c) => {
      const n = Number(c)
      if (!Number.isNaN(n)) codes.add(n)
    })
  }
  return [...codes]
}

/** 车次类型筛选（使用预计算字段，避免大数据量下重复解析） */
const matchesCarTypeFilter = (item, selectedTypes) => {
  if (!selectedTypes?.length) return true
  const selected = selectedTypes.map((v) => Number(v))
  const typeSelected = selected.filter((c) => c <= 5)
  const tagSelected = selected.filter((c) => c >= 6)
  const primary = item._brandCode ?? inferBrandCodeFromTrainNumber(item.trainNumber)

  if (typeSelected.length) {
    if (primary == null || !typeSelected.includes(primary)) {
      return false
    }
  }
  if (tagSelected.length) {
    const brands = item._brandTags ?? getTrainBrandCodes(item)
    if (!tagSelected.some((c) => brands.includes(c))) {
      return false
    }
  }
  return true
}

const enrichTrainItem = (item, index) => ({
  ...item,
  key: `${item.trainId}-${item.departure}-${item.arrival}-${item.departureTime || index}`,
  _brandCode: inferBrandCodeFromTrainNumber(item.trainNumber),
  _departureHour: parseDepartureHour(item.departureTime),
  _brandTags: getTrainBrandCodes(item)
})

const applyHeadSearchFilters = () => {
  const v = toRaw(headSearch)
  const slot = Number(v.departureTimeSlot)
  const hasSlot = slot && slot !== 0
  const ranges = {1: [0, 6], 2: [6, 12], 3: [12, 18], 4: [18, 24]}
  const [lo, hi] = hasSlot ? ranges[slot] || [0, 24] : [0, 24]
  const hasCar = v.car_type?.length > 0
  const hasDep = v.departure?.length > 0
  const hasArr = v.arrival?.length > 0
  const hasSeat = v.seat?.length > 0
  const depSet = hasDep ? new Set(v.departure) : null
  const arrSet = hasArr ? new Set(v.arrival) : null
  const seatSet = hasSeat ? new Set(v.seat.map(Number)) : null
  const raw = rowState.rawTrainList

  let list = raw
  if (hasCar || hasDep || hasArr || hasSeat || hasSlot) {
    list = raw.filter((item) => {
      if (hasCar && !matchesCarTypeFilter(item, v.car_type)) return false
      if (hasDep && !depSet.has(item.departure)) return false
      if (hasArr && !arrSet.has(item.arrival)) return false
      if (hasSeat) {
        const seats = item.seatClassList
        if (!seats?.some((s) => seatSet.has(s.type) && s.quantity)) return false
      }
      if (hasSlot) {
        const h = item._departureHour ?? parseDepartureHour(item.departureTime)
        if (h < lo || h >= hi) return false
      }
      return true
    })
  } else {
    list = raw
  }

  const sortKey = v.sortKey
  if (sortKey && sortKey !== 'default') {
    list = [...list]
    if (sortKey === 'departAsc') {
      list.sort(
        (a, b) =>
          (a._departureHour ?? parseDepartureHour(a.departureTime)) -
          (b._departureHour ?? parseDepartureHour(b.departureTime))
      )
    } else if (sortKey === 'durationAsc') {
      list.sort((a, b) =>
        String(a.duration || '').localeCompare(String(b.duration || ''))
      )
    } else if (sortKey === 'priceAsc') {
      list.sort((a, b) => trainMinPrice(a) - trainMinPrice(b))
    }
  }

  state.trainList = list
  tablePagination.current = 1
  filterApplying.value = false
}

let filterDebounceTimer = null
const scheduleApplyFilters = () => {
  clearTimeout(filterDebounceTimer)
  filterApplying.value = true
  filterDebounceTimer = setTimeout(() => {
    requestAnimationFrame(applyHeadSearchFilters)
  }, 180)
}

watch(
  () => [
    headSearch.car_type,
    headSearch.departure,
    headSearch.arrival,
    headSearch.seat,
    headSearch.departureTimeSlot,
    headSearch.sortKey
  ],
  scheduleApplyFilters,
  { deep: true }
)

const currCityStations = ref([])
const currArrivalStations = ref([])
const checkTagCardType = ref(false)
const departureTagAll = ref(false)
const arrivalTagAll = ref(false)
const seatTagAll = ref(false)

const columns = [
  {
    title: '车次',
    dataIndex: 'trainNumber',
    slots: {customRender: 'trainNumber'},
    key: 'trainNumber',
    // ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    key: 'station',
    slots: {title: 'customStaionTitle', customRender: 'station'},
    ellipsis: true,
    resizeble: false,
    align: 'left'
  },
  {
    dataIndex: 'time',
    key: 'time',
    slots: {title: 'customTimeTitle', customRender: 'time'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '历时',
    dataIndex: 'duration',
    key: 'duration',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'seat',
    slots: {title: 'customSeatTitle', customRender: 'highSpeedTrain'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '一等座',
    dataIndex: 'seatClassList',
    slots: {customRender: 'firstSeat'},
    key: 'first_seat',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'second_seat',
    slots: {title: 'customSecondSeatTitle', customRender: 'secondSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '动卧',
    dataIndex: 'seatClassList',
    key: 'bed',
    slots: {customRender: 'bed'},
    ellipsis: true,
    resizeble: false
  },
  {
    title: '高级软卧',
    dataIndex: 'seatClassList',
    key: 'delux_soft_bed',
    slots: {customRender: 'deluxSoftBed'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'first_bed',
    slots: {title: 'customFirstBedSeatTitle', customRender: 'firstBed'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'scond_hard_bed',
    slots: {title: 'customScondHardSeadTitle', customRender: 'scondHardBed'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '软座',
    dataIndex: 'seatClassList',
    key: 'first_soft_seat',
    slots: {customRender: 'firstSoftSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '硬座',
    dataIndex: 'seatClassList',
    key: 'hard_seat',
    slots: {customRender: 'hardSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '无座',
    dataIndex: 'seatClassList',
    key: 'no_seat',
    slots: {customRender: 'noSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '备注',
    dataIndex: 'remark',
    slots: {customRender: 'operation'},
    align: 'center'
  }
]

const innerColumns = [
  {
    title: '车次',
    key: 'trainNumber',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '车站',
    key: 'station',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '时间',
    key: 'time',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '历时',
    key: 'duration',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'seat',
    slots: {title: 'customSeatTitle', customRender: 'highSpeedTrainPrice'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '一等座',
    dataIndex: 'seatClassList',
    slots: {customRender: 'firstSeatPrice'},
    key: 'first_seat',
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'second_seat',
    slots: {title: 'customSecondSeatTitle', customRender: 'secondSeatPrice'},

    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '动卧',
    dataIndex: 'seatClassList',
    key: 'bed',
    slots: {customRender: 'bedPrice'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '高级软卧',
    dataIndex: 'seatClassList',
    key: 'delux_soft_bed',
    slots: {customRender: 'deluxSoftBedPrice'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'first_bed',
    slots: {title: 'customFirstBedSeatTitle', customRender: 'firstBed'},

    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    dataIndex: 'seatClassList',
    key: 'scond_hard_bed',
    slots: {
      title: 'customScondHardSeadTitle',
      customRender: 'scondHardBedPrice'
    },
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '软座',
    dataIndex: 'seatClassList',
    key: 'first_soft_seat',
    slots: {customRender: 'firstSoftSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '硬座',
    dataIndex: 'seatClassList',
    key: 'hard_seat',
    slots: {customRender: 'hardSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '无座',
    dataIndex: 'seatClassList',
    key: 'no_seat',
    slots: {customRender: 'noSeat'},
    ellipsis: true,
    resizeble: false,
    align: 'center'
  },
  {
    title: '备注',
    dataIndex: 'remark',
    slots: {customRender: 'operation'},
    align: 'center'
  }
]

const cardInfoColumns = [
  {
    title: '站序',
    dataIndex: 'sequence',
    key: 'sequence'
  },
  {
    title: '站名',
    dataIndex: 'departure',
    key: 'departure'
  },
  {
    title: '到站时间',
    dataIndex: 'arrivalTime',
    key: 'arrivalTime'
  },
  {
    title: '出发时间',
    dataIndex: 'departureTime',
    key: 'departureTime'
  },
  {
    title: '停留时间',
    dataIndex: 'stopoverTime',
    key: 'stopoverTime'
  }
]

const {resetFields, validate} = useForm(headSearch)

const days = new Array(15)
    .fill(',')
    .map((item, index) => dayjs().add(index, 'days'))

const handSubmit = () => {
  validate().then(() => {
    state.searchLoading = true
    const raw = toRaw(headSearch)
    const {
      fromStation,
      toStation,
      departure,
      arrival,
      departureDate,
      tripMode,
      returnDate
    } = raw
    if (tripMode === 'transfer') {
      message.info('中转联程需换乘方案与余票联动，已按直达车次查询；详见下方「中转换乘」说明。')
    }
    const mapList = (list) => list?.map(enrichTrainItem) ?? []

    const req = {
      fromStation,
      toStation,
      departure: departure[0],
      arrival: arrival[0],
      departureDate: departureDate.format('YYYY-MM-DD')
    }

    fetchTicketSearch(req)
      .then((res) => {
        if (!res.success) return message.error(res.message)
        state.returnTrainList = []
        if (res.data.trainList) {
          state.trainList = mapList(res.data.trainList)
          rowState.rawTrainList = mapList(res.data.trainList)
        }
        state.quoteTimestamp = res.data.timestamp ?? null
        state.trainBrandListSelect = mergeTrainBrandList(
          res.data.trainBrandList,
          res.data.trainList
        )
        state.seatClassTypeListSelect = res.data.seatClassTypeList
        currCityStations.value = res.data.departureStationList
        currArrivalStations.value = res.data.arrivalStationList
        applyHeadSearchFilters()

        if (tripMode === 'round' && returnDate) {
          return fetchTicketSearch({
            fromStation: toStation,
            toStation: fromStation,
            departure: arrival[0],
            arrival: departure[0],
            departureDate: dayjs(returnDate).format('YYYY-MM-DD')
          }).then((r2) => {
            if (r2.success && r2.data?.trainList) {
              state.returnTrainList = mapList(r2.data.trainList)
            } else if (r2 && !r2.success) {
              message.warning(r2.message || '返程查询无结果')
            }
          })
        }
      })
      .finally(() => {
        state.searchLoading = false
      })
  })
}

/** 首页跳转带入的查询参数同步到表单 */
const applyQueryFromRoute = () => {
  if (route.path !== '/ticketSearch') return
  const q = route.query
  if (typeof q.fromStation === 'string' && q.fromStation) {
    headSearch.fromStation = q.fromStation
  }
  if (typeof q.toStation === 'string' && q.toStation) {
    headSearch.toStation = q.toStation
  }
  if (typeof q.departureDate === 'string' && q.departureDate) {
    const d = dayjs(q.departureDate)
    const invalid =
      !d.isValid() ||
      d < dayjs().startOf('day') ||
      d > dayjs().add(14, 'day')
    if (!invalid) {
      headSearch.departureDate = d
    }
  }

  // 如果是改签流程，提示用户
  if (q.isReschedule === '1' && q.orderSn) {
    message.info('您正在为订单进行车票改签，请选择新车次')
  }
}

watch(
  () => route.fullPath,
  () => {
    if (route.path !== '/ticketSearch') return
    applyQueryFromRoute()
    if (route.query.autoSearch === '1') {
      nextTick(() => handSubmit())
    }
  },
  {immediate: true}
)

const getTitle = (item) => {
  return item.format('MM-DD')
}


const handlePriceShow = (price) => {
  if (price) {
    return '￥' + price
  } else {
    return ''
  }
}

const disabledDate = (current) => {
  // 禁用小于今天的日期和大于15天的日期
  return current && (current < dayjs().startOf('day') || current > dayjs().add(14, 'day'))
}

/** 车站下拉框本地筛选（与首页一致） */
const filterStationOption = (input, option) =>
  (option?.label ?? '').toLowerCase().includes(String(input).toLowerCase())

onMounted(() => {
  fetchStationAll()
    .then((res) => {
      if (res.success && Array.isArray(res.data)) {
        state.stationList = res.data
      }
    })
    .catch(() => {})
})

const exchangeCity = () => {
  const [a, b] = [toRaw(headSearch.fromStation), toRaw(headSearch.toStation)]
  console.log(a, b)

  headSearch.fromStation = b
  headSearch.toStation = a
}

const handleTrainClick = (trainId) => {
  fetchTrainStation({trainId}).then((res) => {
    state.trainStationList = res.data
    state.loading = false
  })
}

const handleBook = (record) => {
  const query = {
    trainNumber: record.trainNumber,
    trainNo: record.trainNumber,
    trainId: record.trainId,
    fromStation: headSearch.fromStation,
    toStation: headSearch.toStation,
    departure: record.departure,
    arrival: record.arrival,
    departureDate: headSearch.departureDate.format('YYYY-MM-DD'),
    quoteTimestamp: state.quoteTimestamp || ''
  }

  if (route.query.isReschedule === '1' && route.query.orderSn) {
    query.isReschedule = '1'
    query.orderSn = route.query.orderSn
  }

  router.push({ path: '/buyTicket', query })
}

/** 返程预订：发到站、时刻与日期与去程对调 */
const returnTripColumns = [
  {title: '车次', dataIndex: 'trainNumber', key: 'trainNumber', align: 'center'},
  {title: '发站', dataIndex: 'departure', key: 'departure', ellipsis: true},
  {title: '到站', dataIndex: 'arrival', key: 'arrival', ellipsis: true},
  {title: '发时', dataIndex: 'departureTime', key: 'departureTime'},
  {title: '到时', dataIndex: 'arrivalTime', key: 'arrivalTime'},
  {title: '历时', dataIndex: 'duration', key: 'duration'},
  {
    title: '操作',
    key: 'returnBook',
    align: 'center',
    slots: {customRender: 'returnBook'}
  }
]

const handleBookReturn = (record) => {
  const hs = toRaw(headSearch)
  const depDate = dayjs(hs.returnDate).format('YYYY-MM-DD')
  router.push({
    path: '/buyTicket',
    query: {
      trainNumber: record.trainNumber,
      trainNo: record.trainNumber,
      trainId: record.trainId,
      fromStation: hs.toStation,
      toStation: hs.fromStation,
      departure: record.arrival,
      arrival: record.departure,
      departureDate: depDate
    }
  })
}
</script>
<template>
  <div class="ticket-search-page">
    <Spin
        :spinning="state.pageLoading"
        tip="正在加载车票信息..."
        size="large"
        class="page-loading-spin"
    >
      <Space direction="vertical" size="middle" :style="{ width: '100%' }">
        <!-- 改签提示条 -->
        <Alert
          v-if="route.query.isReschedule === '1'"
          type="info"
          show-icon
          message="车票改签"
          description="您正在为订单进行车票改签。请选择新车次，完成改签后需在规定时间内支付票款差额。"
          closable
          style="margin-bottom: 12px"
        />
        <Form
            layout="inline"
            :colon="false"
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
        >
          <Space direction="vertical" size="middle" :style="{ width: '100%' }">
            <Card
                :style="{
              width: '100%'
            }"
            >
              <Row :style="{ maxWidth: '1150px' }">
                <Col :span="5">
                  <FormItem label="出发地">
                    <Select
                        v-model:value="headSearch.fromStation"
                        :style="{ width: '150px' }"
                        :show-arrow="false"
                        :show-search="true"
                        :filter-option="filterStationOption"
                        :options="
                      state.stationList.map((item) => ({
                        label: item.name,
                        value: item.name
                      }))
                    "
                    >
                    </Select>
                  </FormItem>
                </Col>
                <Col
                    :span="2"
                    :style="{
                  display: 'flex'
                }"
                >
                  <div
                      :style="{
                    width: '100%',
                    textAlign: 'center',
                    alignItems: 'center',
                    lineHeight: '32px'
                  }"
                  >
                    <RetweetOutlined
                        @click="exchangeCity"
                        :style="{
                      cursor: 'pointer',
                      fontSize: '20px',
                      color: '#1990ff'
                    }"
                    />
                  </div
                  >
                </Col>
                <Col :span="5">
                  <FormItem label="目的地">
                    <Select
                        v-model:value="headSearch.toStation"
                        :style="{ width: '150px' }"
                        :show-arrow="false"
                        :show-search="true"
                        :filter-option="filterStationOption"
                        :options="
                      state.stationList.map((item) => ({
                        label: item.name,
                        value: item.name
                      }))
                    "
                    >
                    </Select>
                  </FormItem>
                </Col>
                <Col :span="5">
                  <FormItem label="出发日">
                    <DatePicker
                        v-model:value="headSearch.departureDate"
                        :style="{ width: '150px' }"
                        :show-arrow="false"
                        :allow-clear="false"
                        :disabled-date="disabledDate"
                    >
                    </DatePicker>
                  </FormItem>
                </Col>
                <Col span="2">
                  <FormItem>
                    <Button
                        type="primary"
                        :loading="state.searchLoading"
                        @click="
                      () => {
                        handSubmit()
                      }
                    "
                    >查询
                    </Button
                    >
                  </FormItem>
                </Col>
              </Row>
              <Row :gutter="[12, 8]" :style="{ marginTop: '10px', maxWidth: '1150px' }">
                <Col :span="24">
                  <FormItem label="查询类型">
                    <RadioGroup v-model:value="headSearch.tripMode" disabled>
                      <Radio value="single">单程</Radio>
                      <Radio value="round">往返</Radio>
                      <Radio value="transfer">中转换乘</Radio>
                    </RadioGroup>
                  </FormItem>
                </Col>
                <Col v-if="headSearch.tripMode === 'round'" :span="8">
                  <FormItem label="返程日期">
                    <DatePicker
                        v-model:value="headSearch.returnDate"
                        :style="{ width: '150px' }"
                        :allow-clear="false"
                        :disabled-date="disabledDate"
                    />
                  </FormItem>
                </Col>
                <Col :span="10">
                  <FormItem label="列表排序">
                    <Select
                        v-model:value="headSearch.sortKey"
                        :style="{ width: '200px' }"
                        :options="[
                          { value: 'default', label: '默认' },
                          { value: 'departAsc', label: '出发早到晚' },
                          { value: 'durationAsc', label: '历时短到长' },
                          { value: 'priceAsc', label: '价格低到高' }
                        ]"
                    />
                  </FormItem>
                </Col>
              </Row>
            </Card>
            <div class="card-container">
              <Tabs
                  type="card"
                  :default-active-key="headSearch.departureDate.format('MM-DD')"
                  :key="headSearch.departureDate.format('MM-DD')"
                  @change="
                (value) => {
                  const year = dayjs().format('YYYY')
                  const date = year + value
                  headSearch.departureDate = dayjs(date)
                }
              "
              >
                <TabPane
                    v-for="item in days"
                    :key="item.format('MM-DD')"
                    :tab="getTitle(item)"
                >
                  <Card flex :bordered="false">
                    <Row justify="space">
                      <Col :span="20" flex>
                        <FormItem>
                          <template v-slot:label>
                            <span class="spacial-label">车次类型</span>
                          </template>
                          <CheckboxGroup
                              :value="headSearch.car_type"
                              @change="
                            (value) => {
                              checkTagCardType =
                                value.length ===
                                state.trainBrandListSelect.length
                              headSearch.car_type = value
                            }
                          "
                          >
                            <CheckableTag
                                :checked="checkTagCardType"
                                @change="
                              (value) => {
                                checkTagCardType = value
                                if (value) {
                                  headSearch.car_type =
                                    state.trainBrandListSelect
                                } else {
                                  headSearch.car_type = []
                                }
                              }
                            "
                                :style="{
                              border: '1px solid #f0f0f0',
                              marginRight: '10px'
                            }"
                            >全部
                            </CheckableTag
                            >
                            <Checkbox
                                v-for="seatItem in state.trainBrandListSelect"
                                :value="
                              TRAIN_BRAND_LIST.find(
                                (item) => item.code === seatItem
                              )?.code
                            "
                            >{{
                                TRAIN_BRAND_LIST.find(
                                    (item) => item.code === seatItem
                                )?.label
                              }}
                            </Checkbox
                            >
                          </CheckboxGroup>
                        </FormItem>
                        <FormItem>
                          <template v-slot:label>
                            <span class="spacial-label">出发车站</span>
                          </template>
                          <CheckboxGroup
                              :value="headSearch.departure"
                              @change="
                            (value) => {
                              departureTagAll =
                                value.length === currCityStations.length
                              headSearch.departure = value
                            }
                          "
                          >
                            <CheckableTag
                                :style="{
                              border: '1px solid #f0f0f0',
                              marginRight: '10px'
                            }"
                                :checked="departureTagAll"
                                @change="
                              (value) => {
                                departureTagAll = value
                                if (value) {
                                  headSearch.departure = currCityStations
                                } else {
                                  headSearch.departure = []
                                }
                              }
                            "
                            >全部
                            </CheckableTag
                            >
                            <Checkbox
                                v-for="item in currCityStations"
                                :value="item"
                            >{{ item }}
                            </Checkbox
                            >
                          </CheckboxGroup>
                        </FormItem>
                        <FormItem>
                          <template v-slot:label>
                            <span class="spacial-label">到达车站</span>
                          </template>
                          <CheckboxGroup
                              :value="headSearch.arrival"
                              @change="
                            (value) => {
                              arrivalTagAll =
                                value.length === currArrivalStations.length
                              headSearch.arrival = value
                            }
                          "
                          >
                            <CheckableTag
                                :checked="arrivalTagAll"
                                :style="{
                              border: '1px solid #f0f0f0',
                              marginRight: '10px'
                            }"
                                @change="
                              (value) => {
                                arrivalTagAll = value
                                if (value) {
                                  headSearch.arrival = currArrivalStations
                                } else {
                                  headSearch.arrival = []
                                }
                              }
                            "
                            >全部
                            </CheckableTag
                            >
                            <Checkbox
                                v-for="item in currArrivalStations"
                                :value="item"
                            >{{ item }}
                            </Checkbox
                            >
                          </CheckboxGroup>
                        </FormItem>
                        <FormItem>
                          <template v-slot:label>
                            <span class="spacial-label">车次席别</span>
                          </template>
                          <CheckboxGroup
                              :style="{ minWidth: '300px', textWrap: 'nowrap' }"
                              :value="headSearch.seat"
                              @change="
                            (value) => {
                              seatTagAll =
                                value.length ===
                                state.seatClassTypeListSelect.length
                              headSearch.seat = value
                            }
                          "
                          >
                            <CheckableTag
                                :checked="seatTagAll"
                                @change="
                              (value) => {
                                seatTagAll = value
                                if (value) {
                                  headSearch.seat =
                                    state.seatClassTypeListSelect
                                } else {
                                  headSearch.seat = []
                                }
                              }
                            "
                                :style="{
                              border: '1px solid #f0f0f0',
                              marginRight: '10px'
                            }"
                            >全部
                            </CheckableTag
                            >
                            <Checkbox
                                v-for="seatItem in state.seatClassTypeListSelect"
                                :value="
                              SEAT_CLASS_TYPE_LIST.find(
                                (item) => item.code === seatItem
                              )?.code
                            "
                            >{{
                                SEAT_CLASS_TYPE_LIST.find(
                                    (item) => item.code === seatItem
                                )?.label
                              }}
                            </Checkbox
                            >
                          </CheckboxGroup>
                        </FormItem>
                      </Col>
                      <Col
                          :span="4"
                          :style="{
                          textAlign: 'end'
                        }"
                      >
                        <FormItem label="发车时间">
                          <Select
                              v-model:value="headSearch.departureTimeSlot"
                              dropdownClassName="custom-select"
                              :options="
                            carRangeTime.map((item) => ({
                              label: item.label,
                              value: item.value
                            }))
                          "
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Card>
                </TabPane>
              </Tabs>
            </div>
          </Space>
        </Form>
        <Row>
          <Col :span="12">
          <span class="city-name">{{ headSearch.fromStation }}</span>
            <SwapRightOutlined/>
            <span class="city-name">{{ headSearch.toStation }}</span>

            （<span class="time-title">{{
              headSearch.departureDate.format('MM月DD日')
            }}</span>
            <span class="time-title">{{
                getWeekNumber(headSearch.departureDate.day())
              }}</span
            >） 共计<span :style="{ fontWeight: 'bolder' }">{{
              state.trainList?.length
            }}</span
          >个车次
          </Col>
        </Row>
        <div class="custome-table">
          <Table
              row-key="key"
              size="small"
              table-layout="auto"
              :columns="columns"
              :data-source="state.trainList"
              :loading="filterApplying"
              :pagination="tablePagination"
          >
            <template #expandedRowRender="{ record }">
              <Table
                  size="small"
                  table-layout="auto"
                  :show-header="false"
                  :pagination="false"
                  :columns="innerColumns"
                  :data-source="[record]"
              >
                <template #highSpeedTrainPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      // (text?.find((item) => item?.type === 0)?.price ||
                      //   '￥' + text?.find((item) => item?.type === 12)?.price) ??
                      // ''
                      handlePriceShow(
                          text?.find((item) => item?.type === 0)?.price ??
                          text.find((item) => item?.type === 12)?.price
                      )
                    }}
                  </div>
                </template>
                <template #firstSeatPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 1)?.price
                      )
                    }}
                  </div>
                </template>
                <template #secondSeatPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 2)?.price ??
                          text?.find((item) => item.type === 3)?.price
                      )
                    }}
                  </div>
                </template>
                <template #bedPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 10)?.price
                      )
                    }}
                  </div>
                </template>
                <template #deluxSoftBedPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 9)?.price
                      )
                    }}
                  </div>
                </template>
                <template #firstBed="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 6)?.price ??
                          text?.find((item) => item?.type === 4)?.price
                      )
                    }}
                  </div>
                </template>
                <template #scondHardBedPrice="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 7)?.price ??
                          text?.find((item) => item?.type === 5)?.price
                      )
                    }}
                  </div>
                </template>
                <template #firstSoftSeat="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          item?.find((item) => item?.type === 11)?.price
                      )
                    }}
                  </div>
                </template>
                <template #hardSeat="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 8)?.price
                      )
                    }}
                  </div>
                </template>
                <template #noSeat="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 13)?.price
                      )
                    }}
                  </div>
                </template>
                <template #other="{ text }">
                  <div :style="{ color: '#fc8302' }">
                    {{
                      handlePriceShow(
                          text?.find((item) => item?.type === 14)?.price
                      )
                    }}
                  </div>
                </template>
              </Table>
            </template>
            <template #trainNumber="{ text, record }">
              <div
                  class="card-name"
                  :style="{
                cursor: 'pointer'
              }"
                  @click="
                () => {
                  state.loading = true
                  handleTrainClick(record.trainId)
                }
              "
              >
                <Tooltip
                    :get-popup-container="(node) => node.parentNode"
                    placement="rightTop"
                    trigger="click"
                >
                  <div class="train-number-badge">
                    {{ text }}
                  </div>
                  <div
                      style="
                    display: flex;
                    font-size: 10px;
                    font-weight: bold;
                    width: 100%;
                    line-height: unset;
                    justify-content: center;
                  "
                  >
                    <div
                        v-for="tag in record?.trainTags"
                        :style="{
                      color: TRAIN_TAG.find((item) => item.value === tag)
                        ?.color,
                      border: `1px solid ${
                        TRAIN_TAG.find((item) => item.value === tag)?.color
                      }`,
                      padding: '2px',
                      aspectRatio: '1 / 1',
                      borderRadius: '2px',
                      marginRight: '4px'
                    }"
                    >
                      {{ TRAIN_TAG.find((item) => item.value === tag)?.label }}
                    </div>
                  </div>
                  <template #title>
                    <Table
                        :columns="cardInfoColumns"
                        :data-source="state.trainStationList"
                        :pagination="false"
                        :loading="state.loading"
                    ></Table>
                  </template>
                </Tooltip>
              </div>
            </template>
            <template #station="{ _, record }">
              <div>
              <span
                  :style="{
                  display: 'inline-block',
                  padding: '2px 4px',
                  backgroundColor: record.departureFlag ? '#cca567' : '#7597d5',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bolder',
                  marginRight: '2px'
                }"
              >{{ record.departureFlag ? '始' : '过' }}</span
              >{{ record.departure }}
              </div>
              <div>
              <span
                  :style="{
                  display: 'inline-block',
                  padding: '2px 4px',
                  backgroundColor: record.arrivalFlag ? '#6da77f' : '#7597d5',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bolder',
                  marginRight: '2px'
                }"
              >{{ record.arrivalFlag ? '终' : '过' }}</span
              >{{ record.arrival }}
              </div>
            </template>
            <template #customStaionTitle>
              <div>出发站</div>
              <div>到达站</div>
            </template>
            <template #time="{ _, record }">
              <div>
                {{ record.departureTime }}
              </div>
              <div>{{ record.arrivalTime }}</div>
            </template>
            <template #highSpeedTrain="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 0)?.quantity ??
                    text?.find((item) => item?.type === 12)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 0)?.quantity
                  )?.label ??
                  getTicketNumber(
                      text?.find((item) => item?.type === 12)?.quantity
                  )?.label ??
                  '--'
                }}
              </div>
            </template>
            <template #firstSeat="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 1)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 1)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #secondSeat="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 2)?.quantity ??
                    text?.find((item) => item?.type === 3)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 2)?.quantity
                  )?.label ??
                  getTicketNumber(
                      text?.find((item) => item?.type === 3)?.quantity
                  )?.label ??
                  '--'
                }}
              </div>
            </template>
            <template #bed="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 10)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 10)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #deluxSoftBed="{ text }">
              <div>
                {{ text?.find((item) => item?.type === 9)?.quantity ?? '--' }}
              </div>
            </template>
            <template #firstBed="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 6)?.quantity ??
                    text?.find((item) => item?.type === 4)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 6)?.quantity
                  )?.label ??
                  getTicketNumber(
                      text?.find((item) => item?.type === 4)?.quantity
                  )?.label ??
                  '--'
                }}
              </div>
            </template>
            <template #scondHardBed="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 7)?.quantity ??
                    text?.find((item) => item?.type === 5)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 7)?.quantity
                  )?.label ??
                  getTicketNumber(
                      text?.find((item) => item?.type === 5)?.quantity
                  )?.label ??
                  '--'
                }}
              </div>
            </template>
            <template #firstSoftSeat="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 11)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 11)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #hardSeat="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 8)?.quantity
                )?.color
              }"
              >
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 8)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #noSeat="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 13)?.quantity
                )?.color
              }"
              >
                <!-- {{ text?.find((item) => item?.type === 13)?.quantity ?? '--' }} -->
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 13)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #other="{ text }">
              <div
                  :style="{
                color: getTicketNumber(
                  text?.find((item) => item?.type === 14)?.quantity
                )?.color
              }"
              >
                <!-- {{ text?.find((item) => item?.type === 14)?.quantity ?? '--' }} -->
                {{
                  getTicketNumber(
                      text?.find((item) => item?.type === 14)?.quantity
                  )?.label ?? '--'
                }}
              </div>
            </template>
            <template #customTimeTitle>
              <div>出发时间</div>
              <div>到达时间</div>
            </template>
            <template #customSecondSeatTitle>
              <div>二等座</div>
              <div>二等包座</div>
            </template>
            <template #customSeatTitle>
              <div>商务座</div>
              <div>特等座</div>
            </template>
            <template #customScondHardSeadTitle>
              <div>硬卧</div>
              <div>二等卧</div>
            </template>
            <template #customFirstBedSeatTitle>
              <div>软卧</div>
              <div>一等卧</div>
            </template>
            <template #operation="{ text, record }">
              <Button
                  class="book-btn"
                  type="link"
                  size="small"
                  @click="
                () => {
                  handleBook(record)
                }
              "
                  :disabled="
                !record.seatClassList.filter((item) => item.quantity)?.length
              "
              >预订
              </Button
              >
            </template>
          </Table
          >
        </div>
        <Alert type="warning">
          <template #description>
            <div>
              如果查询结果中没有满足需求的车次，您还可以使用中转换乘功能，查询途中换乘一次的部分列车余票情况。
            </div>
            <div>
              显示的卧铺票价均为上铺票价，供您参考。具体票价以您确认支付时实际购买的铺别票价为准。如因运力原因或者其他不可控因素导致列车调度调整时，当前车型可能会发生变动
            </div>
          </template>
        </Alert>
        <Card
            v-if="headSearch.tripMode === 'round' && state.returnTrainList.length"
            title="返程车次（发到站已对调）"
            :bordered="false"
            :style="{ marginTop: '12px' }"
        >
          <Table
              size="small"
              table-layout="auto"
              :columns="returnTripColumns"
              :data-source="state.returnTrainList"
              :pagination="false"
          >
            <template #returnBook="{ record }">
              <Button
                  class="book-btn"
                  type="link"
                  size="small"
                  @click="() => handleBookReturn(record)"
              >预订
              </Button>
            </template>
          </Table>
        </Card>
        <Card
            v-if="headSearch.tripMode === 'transfer'"
            title="中转换乘"
            size="small"
            :style="{ marginTop: '12px' }"
        >
          <p>
            真实换乘需根据余票、接续时间、同城/异站换乘规则计算两段行程。当前为演示：已按<strong>直达</strong>查询上方列表，完整「推荐中转方案」需对接后端换乘服务。
          </p>
          <Button type="link" @click="() => message.info('换乘推荐与联程票接口对接后可在此展示')">
            查看换乘规划说明
          </Button>
        </Card>
      </Space>
    </Spin>
  </div>
</template>
<style lang="scss" scoped>
.ticket-search-page {
  padding-bottom: 0;
}

.page-loading-spin {
  width: 100%;

  :deep(.ant-spin-spinning) {
    min-height: 20vh;
  }

  :deep(.ant-spin-spinning .ant-spin-container) {
    min-height: inherit;
  }

  :deep(.ant-spin-text) {
    font-size: 14px;
    font-weight: 500;
    color: #666;
    margin-top: 12px;
  }

  :deep(.ant-spin-dot) {
    font-size: 20px;
  }
}

.card-container {
  border: 1px solid #d9e5f7;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  overflow: hidden;
  padding: 8px;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.spacial-label {
  font-weight: bolder;
  color: #334155;
}

.city-name {
  display: inline-block;
  padding: 0 12px;
  font-weight: bolder;
  color: #0f172a;
}

.time-title {
  display: inline-block;
  padding: 0 8px;
  font-weight: bolder;
  color: #0f172a;
}

::v-deep {
  .custom-select {
    .ant-select-item-option-active::before {
      content: '√';
    }
  }

  .ant-tooltip-inner {
    background-color: #fff;
    padding: 0;
    width: 500px;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
  }

  .ant-tooltip-arrow-content {
    background-color: #3b82f6;
  }

  .ant-tabs {
    border: 1px solid #e7edf7;
    border-top: none;
    border-radius: 0 0 12px 12px;
    overflow: hidden;
    background: #fff;
  }

  .ant-form {
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid #edf2fb;
  }

  .ant-divider-vertical {
    height: 100%;
    border-color: #e7edf7;
  }

  .ant-tabs-card.ant-tabs-top > .ant-tabs-nav .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 0;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    /* // border-right: none; */
  }

  .ant-tabs-nav-wrap {
    width: 100%;

    .ant-tabs-nav-list {
      width: 100%;

      .ant-tabs-tab:first-child {
        border-left: none;
      }

      .ant-tabs-tab {
        backdrop-filter: blur(4px);
        width: 6.5%;
        text-align: center;
        border-top-left-radius: 8px !important;
        border-top-right-radius: 8px !important;
        margin: 0 2px;
        border: 1px solid #e5ebf6;
        background: #f7faff;

        .ant-tabs-tab-btn {
          width: 100%;
          text-align: center;
        }
      }

      .ant-tabs-tab.ant-tabs-tab-active {
        flex-grow: 1.5;
        border-color: #b8d3ff;
        background: linear-gradient(180deg, #fdfefe 0%, #eaf3ff 100%);
      }
    }
  }
}

::v-deep(.custome-table) {
  .ant-table table {
    table-layout: auto !important;
    width: 100%;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  .ant-table-thead > tr > th {
    color: #334155;
    padding: 4px 4px !important;
    font-size: 12px;
    line-height: 1.25;
    white-space: nowrap;
    width: auto !important;
  }

  .ant-table-tbody .ant-table-cell {
    font-size: 13px !important;
    line-height: 1.35;
    padding: 4px 4px !important;
    border-bottom: 1px solid #edf2fb;
    background: #fff;
    width: auto !important;
    white-space: nowrap;
  }

  .ant-table-expanded-row > .ant-table-cell {
    padding: 0 !important;
  }

  .ant-table-expanded-row .ant-table {
    margin: 0 !important;
  }

  .ant-pagination {
    margin: 8px 0 0 !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f8fbff;
  }

  .card-name {
    font-size: 14px !important;
    font-weight: bold;
    line-height: 1.15;
    color: #0f172a;
  }

  .train-number-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    padding: 0 4px;
    border: 1px solid #bfdbfe;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .book-btn {
    border-radius: 999px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 0 8px;
    font-weight: 600;
    font-size: 12px;
  }

  .book-btn[disabled] {
    background: #f8fafc;
    color: #94a3b8;
    border-color: #e2e8f0;
  }
}
</style>
